import { getToken } from "../../../shared/data/storage/authStorage";
import { API_BASE_URL } from "../../../config/api";

/**
 * The one HTTP layer under every client-onboarding API module.
 *
 * Everything here is POST with a Bearer token (the backend's convention for
 * this feature), and every failure becomes an APIError whose message is the
 * most readable sentence the response carried. Before this file existed each
 * data/*Api.ts kept its own copy of this plumbing, and the copies had started
 * to drift — the only real difference between them was WHERE the readable
 * refusal lived in the payload, which is what `refusalKeys` captures.
 */

export class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

export function authHeader(): string {
  const token = getToken();
  if (!token) throw new APIError("No authentication token found", 401);

  return `Bearer ${token}`;
}

/**
 * The readable reason a request was refused.
 *
 * A validation refusal arrives as a 400 whose envelope `message` is only ever
 * "Validation failed" — the sentence the screen should actually show ("debits
 * and credits differ by 40,000.00", "e.pynic is already on this list.") sits
 * under `errors`, at a key each endpoint owns. Callers name the keys their
 * endpoints use; the first one present wins, then the envelope message.
 */
function refusalDetail(payload: any, refusalKeys: string[]): string | undefined {
  for (const key of refusalKeys) {
    const detail = payload?.errors?.[key];
    if (typeof detail === "string" && detail !== "") return detail;
  }

  return typeof payload?.message === "string" && payload.message !== "" ? payload.message : undefined;
}

/** POST a JSON body, get the parsed envelope back. */
export async function postJson<T>(path: string, body: unknown, refusalKeys: string[] = []): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: authHeader() },
      body: JSON.stringify(body),
    });
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError(`Network error: ${error instanceof Error ? error.message : "Unknown error"}`, 500);
  }

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new APIError(
      refusalDetail(payload, refusalKeys) || `HTTP error! status: ${response.status}`,
      response.status,
      payload
    );
  }

  return payload as T;
}

/**
 * POST multipart form data — the sheet uploads.
 *
 * No Content-Type header on purpose: the browser has to set it itself because
 * only it knows the boundary string. Setting it by hand produces a body the
 * server cannot split.
 */
export async function postForm<T>(path: string, form: FormData, refusalKeys: string[] = []): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: { Authorization: authHeader() },
    body: form,
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new APIError(
      refusalDetail(payload, refusalKeys) || `Upload failed (HTTP ${response.status})`,
      response.status,
      payload
    );
  }

  return payload as T;
}

/**
 * POST for a spreadsheet and save it to the user's machine.
 *
 * A POST returning a blob rather than a link, because the endpoint needs the
 * bearer token and an `<a href>` cannot carry one. The object URL is revoked
 * straight after: without that, the whole file stays in memory until the tab
 * is closed. The server names the file via Content-Disposition;
 * `fallbackFilename` is only for a stripped header.
 */
export async function downloadSheet(path: string, body: unknown, fallbackFilename: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: authHeader() },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    // The failure path IS json — only the success path is a spreadsheet.
    const payload = await response.json().catch(() => ({}));
    throw new APIError(
      refusalDetail(payload, []) || `Could not build the sheet (HTTP ${response.status})`,
      response.status,
      payload
    );
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);

  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = filenameFrom(response.headers.get("Content-Disposition")) ?? fallbackFilename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

function filenameFrom(disposition: string | null): string | undefined {
  return /filename="([^"]+)"/.exec(disposition ?? "")?.[1];
}
